import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import {
  useSystemStatus,
  useDatabaseConnection,
  useAiConnection,
  useSmtpConnection,
  useSystemMetrics,
  useRefreshSystemStatus,
  useHealthCheck
} from '@/features/stats/hooks'
import { useStorageConfig } from '@/features/data-management/hooks'
import {
  Server,
  HardDrive,
  Cpu,
  MemoryStick,
  RefreshCw,
  CheckCircle,
  XCircle,
  Monitor,
  Brain,
  Mail,
  Info,
  Database,
  Cloud,
  FileArchive
} from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { StorageUsageBar } from '@/features/data-management/components/storage-usage-bar'

interface ConnectionData {
  name: string
  status: 'connected' | 'disconnected'
  message: string
}

function ConnectionRow({
  data,
  isLoading,
  isFetching,
  icon,
  onRetry
}: {
  data: ConnectionData | undefined
  isLoading: boolean
  isFetching: boolean
  icon: React.ReactNode
  onRetry: () => void
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center space-x-4">
          {icon}
          <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
        </div>
        <div className="h-5 w-20 bg-gray-200 animate-pulse rounded" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center space-x-4">
          {icon}
          <span className="text-sm text-gray-500">Unable to fetch status</span>
        </div>
        <Button size="sm" variant="ghost" onClick={onRetry} title="Retry connection">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex items-center space-x-4">
        {icon}
        <div>
          <div className="font-medium">{data.name}</div>
          <div className="text-sm text-gray-600">{data.message}</div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {data.status === 'connected' ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" />
        )}
        <Badge variant={data.status === 'connected' ? 'default' : 'destructive'}>
          {data.status === 'connected' ? 'Connected' : 'Disconnected'}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRetry}
          disabled={isFetching}
          title="Retry connection"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  if (bytes < 0) return 'Unlimited'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

export function BackendManagement() {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // System status query
  const { data: systemStatus, isLoading: statusLoading } = useSystemStatus()

  // Individual connection checks
  const dbConn = useDatabaseConnection()
  const aiConn = useAiConnection()
  const smtpConn = useSmtpConnection()

  // System metrics query
  const { data: systemMetrics, isLoading: metricsLoading } = useSystemMetrics()

  // Storage config
  const { data: storageConfig, isLoading: storageLoading } = useStorageConfig()
  
  // Health check
  const healthCheck = useHealthCheck()

  // Refresh mutation
  const refreshAllMutation = useRefreshSystemStatus()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshAllMutation.mutateAsync()
      toast({
        title: "System Refreshed",
        description: "All system statuses have been refreshed.",
      })
      queryClient.invalidateQueries({ queryKey: ['stats', 'backend'] })
    } catch (error: any) {
      toast({
        title: "Refresh Failed",
        description: error.response?.data?.error || "Failed to refresh system status.",
        variant: "destructive",
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const isLoading = statusLoading || metricsLoading || storageLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Server className="h-5 w-5 mr-2" />
            Backend Management
          </h2>
          <p className="text-gray-600">Monitor backend connections and system health</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh All'}
        </Button>
      </div>

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor className="h-5 w-5" />
            <span>Connection Status</span>
          </CardTitle>
          <CardDescription>
            Live connectivity status for backend services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <ConnectionRow
              data={healthCheck.data ? {
                name: 'API Endpoint',
                status: 'connected',
                message: `Connected — Version ${healthCheck.data.version}`
              } : (healthCheck.isError ? {
                name: 'API Endpoint',
                status: 'disconnected',
                message: 'API is currently unreachable'
              } : undefined)}
              isLoading={healthCheck.isLoading}
              isFetching={healthCheck.isFetching}
              icon={<Server className="h-5 w-5 text-gray-400" />}
              onRetry={() => healthCheck.refetch()}
            />
            <ConnectionRow
              data={dbConn.data}
              isLoading={dbConn.isLoading}
              isFetching={dbConn.isFetching}
              icon={<Database className="h-5 w-5 text-blue-500" />}
              onRetry={() => dbConn.refetch()}
            />
            <ConnectionRow
              data={aiConn.data}
              isLoading={aiConn.isLoading}
              isFetching={aiConn.isFetching}
              icon={<Brain className="h-5 w-5 text-purple-500" />}
              onRetry={() => aiConn.refetch()}
            />
            <ConnectionRow
              data={smtpConn.data}
              isLoading={smtpConn.isLoading}
              isFetching={smtpConn.isFetching}
              icon={<Mail className="h-5 w-5 text-orange-500" />}
              onRetry={() => smtpConn.refetch()}
            />
            {storageConfig && (
              <ConnectionRow
                data={{
                  name: 'Storage Backend',
                  status: storageConfig.status === 'connected' ? 'connected' : 'disconnected',
                  message: storageConfig.status === 'connected' 
                    ? `Connected to ${storageConfig.backend.toUpperCase()} (${formatBytes(storageConfig.used_size_bytes)} used)` 
                    : (storageConfig.error_message || 'Storage connection error')
                }}
                isLoading={storageLoading}
                isFetching={false}
                icon={storageConfig.backend === 'local' 
                  ? <HardDrive className="h-5 w-5 text-gray-500" /> 
                  : <Cloud className="h-5 w-5 text-sky-500" />
                }
                onRetry={() => queryClient.invalidateQueries({ queryKey: ['storage-config'] })}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Cpu className="h-5 w-5" />
            <span>System Metrics</span>
          </CardTitle>
          <CardDescription>
            Real-time system resource usage
          </CardDescription>
        </CardHeader>
        <CardContent>
          {systemMetrics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Cpu className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">CPU Usage</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {(systemMetrics.cpu_usage ?? 0).toFixed(1)}%
                </div>
                {/* Spacer to align with other columns that have used/total text */}
                <div className="text-[10px] leading-tight text-transparent select-none">
                  &nbsp;
                </div>
                <Progress value={systemMetrics.cpu_usage ?? 0} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <MemoryStick className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Memory Usage</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {(systemMetrics.memory_usage ?? 0).toFixed(1)}%
                </div>
                <div className="text-[10px] text-gray-500 flex justify-between">
                  <span>{systemMetrics.memory_used_gb ?? 0} GB used</span>
                  <span>{systemMetrics.memory_total_gb ?? 0} GB total</span>
                </div>
                <Progress value={systemMetrics.memory_usage ?? 0} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <HardDrive className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">System Disk</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {(systemMetrics.disk_usage ?? 0).toFixed(1)}%
                </div>
                <div className="text-[10px] text-gray-500 flex justify-between">
                  <span>{systemMetrics.disk_used_gb ?? 0} GB used</span>
                  <span>{systemMetrics.disk_total_gb ?? 0} GB total</span>
                </div>
                <Progress value={systemMetrics.disk_usage ?? 0} className="h-2" />
              </div>

              {storageConfig && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <FileArchive className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium">Storage Backend</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {storageConfig.total_size_bytes > 0 
                      ? `${((storageConfig.used_size_bytes / storageConfig.total_size_bytes) * 100).toFixed(1)}%`
                      : formatBytes(storageConfig.used_size_bytes)
                    }
                  </div>
                  <div className="text-[10px] text-gray-500 flex justify-between">
                    <span>{formatBytes(storageConfig.used_size_bytes)} used</span>
                    {storageConfig.total_size_bytes > 0 && <span>{formatBytes(storageConfig.total_size_bytes)} total</span>}
                  </div>
                  <StorageUsageBar 
                    totalBytes={storageConfig.total_size_bytes}
                    usedBytes={storageConfig.used_size_bytes}
                    backupsBytes={storageConfig.backups_size_bytes}
                    attachmentsBytes={storageConfig.attachments_size_bytes}
                    templatesBytes={storageConfig.templates_size_bytes}
                    className="pt-1"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              Unable to fetch system metrics
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Info className="h-5 w-5" />
            <span>System Information</span>
          </CardTitle>
          <CardDescription>
            Server environment details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {systemStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</span>
                <div className="text-sm font-medium">{systemStatus.platform}</div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Python Version</span>
                <div className="text-sm font-medium">{systemStatus.python_version}</div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Hostname</span>
                <div className="text-sm font-medium">{systemStatus.hostname}</div>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Uptime</span>
                <div className="text-sm font-medium">{systemStatus.uptime || 'Unknown'}</div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              Unable to fetch system information
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
